import { concat, path } from 'ramda'
import dig from 'spiceutils/dist/dig'

const pathFunc = path

export default class FetchPath {
  /**
   * Constructor.
   *
   * @param {function} fetch
   * @param {function} buildUrl
   * @param {string} URL
   * @param {HttpCache} httpCache
   * @param {AuthInitialiser} authInitialiser
   */
  constructor(fetch, buildUrl, URL, httpCache, authInitialiser) {
    this.fetch = fetch
    this.buildUrl = buildUrl
    this.URL = URL
    this.httpCache = httpCache
    this.authInitialiser = authInitialiser
  }

  /**
   * Fetch a path from the API
   * @param { Object } config - configuration object
   * @param { String } [config.method] - HTTP method
   *   - defaults to 'GET'
   * @param { String } config.path - path to fetch relative to the API root
   * @param { Object } [config.parameters] - key-value paired search string parameters
   *   - defaults to undefined
   * @param { String | String[] } [config.parameters[]] - value of the associated search parameter
   *   - blank values are ignored (see spiceutils blank docs for full rules)
   *   - values are URI encoded
   *   - if an array is given, parameters are broken up to `key[]=item1&key[]=item2...`
   * @param { Any } [config.data] - Body to be sent with the request
   *   - defaults to undefined
   *   - should only be supplied when given method supports a request body (POST, PUT, ...)
   * @param { String } [config.anchor] - anchor to apply to URL
   *   - defaults to undefined
   *   - this most likely has no effect
   * @param { Any | Any[] } [config.jsonPath] - path to dig to within response JSON
   *   - defaults to undefined
   *   - e.g.: if the response contains data at `content.data`, you could pass `['content', 'data']`
   *     and promise will return the data at `response.content.data` if it exists.
   *   - see spiceutils dig docs for full usage
   *   @param { Boolean } retryOnAuthError - Permit the request to be re-run when an auth error is encountered.
   */
  async run(
    {
      method = 'GET',
      path,
      parameters = undefined,
      data,
      anchor = undefined,
      jsonPath = undefined,
      headers = {},
      retryOnAuthError = true,
      earliestPermittedCacheHit = null,
    },
  ) {
    let isPartialCacheResult
    let cacheResult

    if (path[0] !== '/') {
      // eslint-disable-next-line no-param-reassign
      path = `/${path}`
    }

    if (method === 'GET' || method === 'get') {
      const cacheObject = this.httpCache.read(path, parameters, earliestPermittedCacheHit)
      isPartialCacheResult = cacheObject.isPartialResult
      cacheResult = cacheObject.result

      if (!isPartialCacheResult && cacheResult) {
        return cacheResult
      }
    }

    const response = await this.fetch(
      this.buildUrl(`${this.URL}${path}`, parameters, anchor),
      {
        method,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          Accept: 'application/vnd.api+json',
          ...headers,
        },
        body: JSON.stringify(data),
      },
    ).catch((e) => e)

    /*
     * If the user encounters a 401 retry auth and rerun request.
     * This exists to handle situations where the JWT token has expired, which is does at midnight.
     */
    if (retryOnAuthError && pathFunc(['status'], response) === 401) {
      await this.authInitialiser.run()
      return this.run({
        method,
        path,
        parameters,
        data,
        anchor,
        jsonPath,
        headers,
        retryOnAuthError: false,
      })
    }

    if (response instanceof Error) {
      try {
        response.json = JSON.parse(response.message)
      } catch (e) {
      }
      throw response
    }

    let json = await response.json().catch(() => null)

    if (!response.ok) {
      if (json) {
        const error = new Error(JSON.stringify(json, null, 2))
        error.json = json
        throw error
      }
      throw new Error('Error making request')
    }

    if (jsonPath) {
      json = dig(json, ...jsonPath)
    }

    // Merge the partial cache result

    if (method === 'GET' || method === 'get') {
      if (cacheResult) {
        json = concat(json, cacheResult)
      }

      this.httpCache.write(path, parameters, json)
    }

    return json
  }
}
